import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { API_CONFIG } from '../../../config/api.config';
import {
  CreateLeadRequest,
  LeadFlowMetadata,
  LeadInsurabilityResponse,
} from '../../../domain/interfaces/lead/lead.interface';
import { environment } from '../../../../../environments/environment';
import { PetQuotation } from '../../../../features/quoter/models/quotation.model';
import { QuoterStateService } from '../../../../features/quoter/services/quoter-state.service';
import { StorageService } from '../storage/storage.service';
import { CampaignAttributionService } from '../campaign/campaign-attribution.service';
import { resolvePetAgeLabel } from '../../../../shared/utils/pet-age.util';
import {
  isTerminalLeadRoute,
  resolveActiveLeadMetadata,
  resolveLeadFlowMetadata,
} from '../../../../shared/utils/lead-flow-metadata.util';

interface OwnerDataShape {
  leadId?: unknown;
  primerNombre?: unknown;
  segundoNombre?: unknown;
  primerApellido?: unknown;
  segundoApellido?: unknown;
  nombres?: unknown;
  apellidos?: unknown;
  numeroDocumento?: unknown;
  email?: unknown;
  celular?: unknown;
  fechaExpedicion?: unknown;
  birthCountryId?: unknown;
  direccion?: unknown;
  pep?: unknown;
}

@Injectable({ providedIn: 'root' })
export class LeadUnloadUpdateService implements OnDestroy {
  private readonly quoterState = inject(QuoterStateService);
  private readonly storageService = inject(StorageService);
  private readonly campaignAttributionService = inject(CampaignAttributionService);
  private readonly router = inject(Router);

  private lastDispatchAt = 0;
  private lastDispatchKey = '';
  private routerSubscription: Subscription | null = null;
  private currentFlowMetadata: LeadFlowMetadata | null = null;

  init(): void {
    if (this.routerSubscription || typeof window === 'undefined') {
      return;
    }

    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe((event) => this.handleRouteChange(event.urlAfterRedirects || event.url));
    this.handleRouteChange(this.router.url);
  }

  stop(): void {
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private handleRouteChange(route: string): void {
    const url = new URL(route.replace(/^\//, ''), document.baseURI).href;
    const petQuestions = this.quoterState.petQuestionsData() as Record<string, unknown> | null;
    const metadata = resolveLeadFlowMetadata(url, petQuestions);
    
    // Store metadata for current flow route (used on pagehide/beforeunload)
    this.currentFlowMetadata = metadata;

    const activeMetadata = resolveActiveLeadMetadata(url);
    if (activeMetadata) {
      this.tryDispatchUpdateOnUnload(activeMetadata);
      return;
    }
    
    // Also send update if navigating to a terminal route
    if (isTerminalLeadRoute(route)) {
      this.tryDispatchUpdateOnUnload(metadata);
    }
  }

  tryDispatchUpdateOnUnload(metadata?: LeadFlowMetadata): void {
    if (typeof window === 'undefined' || typeof fetch !== 'function') {
      return;
    }

    // ✅ Si estamos en el flujo de "continue online" desde inactivity-timeout, no actualizar
    // porque ya se actualizó en InactivityTimeoutPage.updateLeadAndNavigate()
    const skipUpdate = this.storageService.getSessionItem<string>('__SKIP_LEAD_UNLOAD_UPDATE__');
    if (skipUpdate === 'true') {
      console.log('[LeadUnloadUpdateService] Skipping lead update - already updated from continue-online');
      return;
    }

    const ownerData = (this.quoterState.ownerData() as OwnerDataShape | null) ?? null;
    if (!ownerData) {
      return;
    }

    const leadId = String(ownerData?.leadId ?? '').trim();
    if (!leadId) {
      return;
    }

    // Use provided metadata, or fall back to current flow metadata only
    // Don't auto-resolve from current URL - require explicit metadata or prior navigation
    const metadataToUse = metadata || this.currentFlowMetadata;
    if (!metadataToUse) {
      return; // No metadata available - probably called before any flow navigation
    }
    
    const request = this.buildRequest(ownerData, this.quoterState.getAllPetsForSubmission(), metadataToUse);
    if (!request) {
      return;
    }

    const now = Date.now();
    const dispatchKey = JSON.stringify([leadId, request]);
    if (this.lastDispatchKey === dispatchKey && now - this.lastDispatchAt < 1500) {
      return;
    }

    const token = this.storageService.getLocalItem<string>(environment.auth.cookieKey) ?? '';
    if (!token) {
      return;
    }

    this.lastDispatchAt = now;
    this.lastDispatchKey = dispatchKey;

    const endpoint = `${environment.apiBaseUrl}${API_CONFIG.ENDPOINTS.CREAR_LEAD}/${leadId}`;

    // keepalive allows the browser to continue this request while the page is closing.
    void fetch(endpoint, {
      method: 'PUT',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    }).then((response) => {
      if (!response.ok && this.lastDispatchKey === dispatchKey) {
        this.lastDispatchKey = '';
      }
    }).catch(() => {
      if (this.lastDispatchKey === dispatchKey) {
        this.lastDispatchKey = '';
      }
    });
  }

  private buildRequest(ownerData: OwnerDataShape, pets: PetQuotation[], metadata: LeadFlowMetadata): CreateLeadRequest | null {
    if (!ownerData || pets.length === 0) {
      return null;
    }

    const firstName = this.resolveFirstName(ownerData);
    const middleName = this.resolveMiddleName(ownerData);
    const lastName = this.resolveLastName(ownerData);
    const secondLastName = this.resolveSecondLastName(ownerData);
    const idNumber = String(ownerData.numeroDocumento ?? '').trim();
    const email = String(ownerData.email ?? '').trim();
    const mobilePhone = String(ownerData.celular ?? '').trim();
    const idExpeditionDate = String(ownerData.fechaExpedicion ?? '').trim();
    const birthCountryId = String(ownerData.birthCountryId ?? 'COL').trim() || 'COL';
    const residencyAddress = String(ownerData.direccion ?? '').trim();
    const circulationZoneId = String(pets[0]?.petInfo.cityId ?? '').trim();
    const campaign = this.campaignAttributionService.getLeadCampaignFields();

    if (!firstName || !lastName || !idNumber || !email || !mobilePhone || !idExpeditionDate || !circulationZoneId) {
      return null;
    }

    return {
      data: {
        ...metadata,
        holder: {
          id_type: 'C',
          id_number: idNumber,
          first_name: firstName,
          middle_name: middleName || undefined,
          last_name: lastName,
          second_last_name: secondLastName || undefined,
          email,
          mobile_phone: mobilePhone,
          birth_date: '1900-01-01',
          id_expedition_date: idExpeditionDate,
          birth_country_id: birthCountryId,
          circulation_zone_id: circulationZoneId,
          residency_address: residencyAddress,
          is_pep: ownerData.pep === true,
          assessorCode: this.storageService.getSessionItem<string>('codigoAsesor') ?? '',
          ...(campaign.utm_source ? { utm_source: campaign.utm_source } : {}),
          ...(campaign.utm_medium ? { utm_medium: campaign.utm_medium } : {}),
          ...(campaign.utm_campaign ? { utm_campaign: campaign.utm_campaign } : {}),
          ...(campaign.utm_term ? { utm_term: campaign.utm_term } : {}),
          ...(campaign.utm_content ? { utm_content: campaign.utm_content } : {}),
          ...(campaign.gclid ? { gclid: campaign.gclid } : {}),
        },
        insured_items: pets.map((pet) => ({
          ...(pet.petInfo.insuranceId && { Id: pet.petInfo.insuranceId }),
          name: pet.petInfo.name,
          breed_id: String(pet.petInfo.breedId ?? pet.petInfo.breed ?? ''),
          admission_age: resolvePetAgeLabel(pet.petInfo.age),
          gender: pet.petInfo.sex === 'male' ? 'Macho' : 'Hembra',
          circulation_zone_id: String(pet.petInfo.cityId ?? ''),
          insurability_responses: this.mapInsurabilityResponses(pet),
        })),
      },
      productName: 'SaludAnimal',
    };
  }

  private mapInsurabilityResponses(pet: PetQuotation): LeadInsurabilityResponse[] {
    const entries = Object.entries(pet.petQuestions ?? {});
    return entries
      .filter((entry): entry is [string, boolean] => entry[0] !== 'truthDeclaration' && entry[1] !== null)
      .map(([questionId, response]) => ({
        question_id: questionId,
        question: questionId,
        response,
      }));
  }

  private resolveFirstName(ownerData: OwnerDataShape): string {
    const direct = String(ownerData.primerNombre ?? '').trim();
    if (direct) {
      return direct;
    }

    const names = String(ownerData.nombres ?? '').trim().split(/\s+/).filter(Boolean);
    return names[0] ?? '';
  }

  private resolveMiddleName(ownerData: OwnerDataShape): string {
    const direct = String(ownerData.segundoNombre ?? '').trim();
    if (direct) {
      return direct;
    }

    const names = String(ownerData.nombres ?? '').trim().split(/\s+/).filter(Boolean);
    return names.slice(1).join(' ');
  }

  private resolveLastName(ownerData: OwnerDataShape): string {
    const direct = String(ownerData.primerApellido ?? '').trim();
    if (direct) {
      return direct;
    }

    const surnames = String(ownerData.apellidos ?? '').trim().split(/\s+/).filter(Boolean);
    return surnames[0] ?? '';
  }

  private resolveSecondLastName(ownerData: OwnerDataShape): string {
    if (ownerData.segundoApellido !== undefined && ownerData.segundoApellido !== null) {
      return String(ownerData.segundoApellido).trim();
    }

    const surnames = String(ownerData.apellidos ?? '').trim().split(/\s+/).filter(Boolean);
    return surnames.slice(1).join(' ');
  }
}
